import type { FormikHelpers } from 'formik';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import type { DonationFundFragment } from '@graphql/fragments/registry.graphql.generated';
import { CustomItemDataType, DonationFundPlatformTypeEnum } from '@graphql/generated-base';
import {
  useAddRegistryItemPhotoMutation,
  useCreateDonationFundPlatformMutation,
  useCreateRegistryItemMutation,
  useUpdateRegistryItemMutation,
  useupdateRegistryItemQuantityMutation,
} from '@graphql/mutations/registry.graphql.generated';
import { useTranslation as useReactTranslation } from 'react-i18next';
import { useTranslation } from '@shared/core/i18n';
import type { EditGiftDailogFields } from './types';
import type { CookedProduct } from '@apps/registry/common/selectors/ProductListSelector';
import { isPricedProduct } from '@apps/registry/common/selectors/ProductListSelector';
import type { OnRemoveRegistryItemHandler } from '@apps/registry/admin/Registry.types';
import { getCurrencySymbolFromCurrencyCode } from '@shared/utils/currency/utils';
import { DEFAULT_CURRENCY_SYMBOL } from '@shared/utils/currency/constants';
import { useFilestack } from '@shared/utils/filestack';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMount } from '@shared/utils/hooks/useMount';
import { useTimeout } from '@shared/hooks/useTimeout';
import { useSharedRegistryTranslations } from '@apps/registry/SharedRegistry.i18n';
import type { PaymentMethodOption } from '@shared/utils/paymentMethodOptions';
import { paymentMethodOptions } from '@shared/utils/paymentMethodOptions';
import type { DonationFundInfoFragmentWithUrl } from '@apps/registry/common/components/Catalog/components/ProductGrid/ProductGrid.controller';
import { createPaymentMetadata, getExistingPlatform, getPaymentLink } from '@apps/registry/common/components/AddCashFund/helpers';
import { useIdentify } from '@shared/core';
import { useAdminRegistryTelemetry } from '@apps/registry/admin/AdminRegistry.telemetry';
import { useTrackImpactRadius } from '@shared/utils/impactRadius/useTrackImpactRadius';
import { useCatalogRouting } from '@apps/registry/common/components/Catalog/useCatalogRouting';
import axios from 'axios';
import { useShopStorefrontContext } from '@apps/registry/common/components/Catalog/components/ShopStorefrontProvider';
import { RegistryItemSource } from '@apps/registry/common/components/Catalog/Catalog.constants';
import { useAppPaymentDisclaimerDialogController } from '@apps/registry/common/components/AddCashFund/AppPaymentDisclaimerDialog.controller';
import { isValidUrlAndDomain } from '@shared/utils/urlHelpers';
import { addSuppressedError } from '@shared/utils/logger';

type EditDialogControllerProps = Readonly<{
  eventId: string;
  product: CookedProduct | null;
  showAddressReminderDialog?: () => void;
  onRemove?: OnRemoveRegistryItemHandler;
  isOpen: boolean;
  showCurrencyDialog: () => void;
  onClose: () => void;
  donationFundInfo?: DonationFundInfoFragmentWithUrl;
  prefilledUrl?: string;
}>;

export type ExtractedProduct = Readonly<{
  name: string;
  price: string;
  currency: string;
  /** Symbol */
  currencyRaw: string;
  /** MSRP */
  regularPrice: string;
  url: string;
  mainImage: Readonly<{
    url: string;
  }>;
}>;

export type PurchaseOption = 'cash' | 'groupGift' | 'external';
export const useEditGiftDialogController = ({
  eventId,
  product,
  onRemove,
  onClose,
  showAddressReminderDialog,
  showCurrencyDialog,
  donationFundInfo,
  prefilledUrl,
}: EditDialogControllerProps) => {
  const { t: translateForm } = useReactTranslation('generalFormValidation');
  const { t } = useTranslation('sharedRegistry');
  const { linkInvalid, quantityMinError, receivedQuantityError } = t('giftDialog');

  const { getCashFundDialogTranslations } = useSharedRegistryTranslations();
  const { selectPaymentEmptyError, invalidUsernameIsEmailError, invalidUsernameError } = getCashFundDialogTranslations();

  const { identify } = useIdentify();

  const [createDonationFundPlatform] = useCreateDonationFundPlatformMutation();
  const trackImpactRadius = useTrackImpactRadius();

  const { isBabyStorefront } = useShopStorefrontContext();

  const [updatedPhoto, setUpdatedPhoto] = useState<string | undefined>(undefined);
  const [photoFilename, setPhotoFilename] = useState<string | undefined>(undefined);
  const [isPaymentMethodWarningShown, setIsPaymentMethodWarningShown] = useState<boolean>(false);
  const [prefilledData, setPrefilledData] = useState<ExtractedProduct>();
  const [isProductExtractionLoading, setIsProductExtractionLoading] = useState(false);
  const [showExtractError, setShowExtractError] = useState(false);

  const [createRegistryItem, { loading: createRegistryItemLoading }] = useCreateRegistryItemMutation({
    refetchQueries: () => ['GetAdminRegistryRegistries', 'GetRegistryOnboardingSuggestions', 'RegistryGiftBasketCountByEventName'],
    awaitRefetchQueries: true,
  });
  const [updateRegistryItem, { loading: updateRegistryItemLoading }] = useUpdateRegistryItemMutation({
    refetchQueries: () => ['GetAdminRegistryRegistries'],
  });

  const [updateRegistryItemTotalQuantity, { loading: updateRegistryItemQuantityLoading }] = useupdateRegistryItemQuantityMutation();

  const [uploadPhoto] = useAddRegistryItemPhotoMutation({
    refetchQueries: () => ['GetAdminRegistryRegistries'],
  });
  const qtyReceived = (product?.reserved || 0) + (product?.purchased || 0);

  const { editRegistryItemSaveButton, manualProductDataExtractionAttempt, clickAddRegistryItem } = useAdminRegistryTelemetry();

  useEffect(() => {
    setIsPaymentMethodWarningShown(false);
  }, [product?.id]);

  const { goToBrandPage } = useCatalogRouting();

  const handleBrandClick = useCallback(() => {
    if (product?.brand) {
      const route = isBabyStorefront ? '/shop/baby/brand' : '/shop/brand';
      goToBrandPage(`${route}/${product?.brand.toLowerCase()}`);
    }
  }, [goToBrandPage, isBabyStorefront, product?.brand]);

  const hasAttemptedDataExtraction = useRef(false);
  const abortControllerRef = useRef<AbortController>();
  const errorDismissTimeout = useTimeout();

  const extractDataFromUrl = useCallback(
    async (url: string) => {
      if (!url?.trim() || product?.id) return;

      abortControllerRef.current?.abort();
      const controller = new AbortController(); // eslint-disable-line compat/compat -- polyfilled in src/main/polyfills.ts
      abortControllerRef.current = controller;

      setPrefilledData(undefined);
      setIsProductExtractionLoading(true);
      hasAttemptedDataExtraction.current = true;
      let success = false;
      let resp = undefined;

      try {
        // extract Product data from the URL / page to gain initial input values
        resp = await axios.get<ExtractedProduct>('/api/product/extract', {
          params: { url },
          signal: controller.signal,
        });

        setPrefilledData({ ...resp.data, regularPrice: resp.data.regularPrice || resp.data.price });
        success = true;
      } catch (error) {
        if (axios.isCancel(error)) return;
        setShowExtractError(true);
        errorDismissTimeout.set(() => {
          setShowExtractError(false);
        }, 5000);
      } finally {
        if (controller.signal.aborted) return;
        let domain = 'unknown';
        try {
          domain = new URL(url).hostname;
        } catch (urlError) {
          addSuppressedError(urlError, { url, source: 'extractDataFromUrl' });
        }
        manualProductDataExtractionAttempt({
          domain,
          success,
          productExtractedData: (resp?.data as ExtractedProduct) || undefined,
        });
        setIsProductExtractionLoading(false);
      }
    },
    [errorDismissTimeout, manualProductDataExtractionAttempt, product?.id],
  );

  useEffect(() => {
    if (prefilledUrl) {
      extractDataFromUrl(prefilledUrl);
    }
  }, [extractDataFromUrl, prefilledUrl]);

  useMount(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  });

  const handleSubmit = async (editGiftDialogFields: EditGiftDailogFields, actions: FormikHelpers<EditGiftDailogFields>) => {
    setIsPaymentMethodWarningShown(false);
    const priceToFix = editGiftDialogFields.price ? +editGiftDialogFields.price : 0;

    if (product?.id) {
      const productId = product.id;
      if (product.requested !== editGiftDialogFields.qty) {
        await updateRegistryItemTotalQuantity({
          variables: {
            id: productId,
            quantity: editGiftDialogFields.qty,
          },
        });
      }

      const paymentLink = getPaymentLink(editGiftDialogFields.paymentMethod, editGiftDialogFields.username);

      const getUpdateGiftVariables = (platformId?: Maybe<string>) => ({
        id: productId,
        payload: {
          totalRequested: editGiftDialogFields.qty,
          isGroupGiftingEnabled: editGiftDialogFields.qty === 1 ? editGiftDialogFields.isGroupGiftingEnabled : false,
          note: editGiftDialogFields.note,
          isGetCashEquivalent: editGiftDialogFields.isGetCashEquivalentEnabled,
          donationFundPayload:
            editGiftDialogFields.isGroupGiftingEnabled && product?.numberPrice && editGiftDialogFields.qty === 1
              ? {
                  eventId,
                  fundType: CustomItemDataType.cash,
                  platformId,
                  title: product.title,
                  url: paymentLink,
                }
              : undefined,
          custom: isCatalogItem
            ? undefined
            : {
                type: CustomItemDataType.gift,
                title: editGiftDialogFields.title || '',
                url: editGiftDialogFields.link || '',
                priceAsFloatingPointDecimalString: priceToFix.toFixed(2),
              },
          mustHave: editGiftDialogFields.mustHave,
        },
      });

      const platform = product.donationFund?.fund.platform;
      const existingPlatform = donationFundInfo?.platforms.find(
        platform => platform.type === editGiftDialogFields.paymentMethod && platform.username === editGiftDialogFields.username,
      );

      const shouldCreateNewPlatform = !!(
        editGiftDialogFields.isGroupGiftingEnabled &&
        !existingPlatform &&
        (platform?.id ? platform?.type !== editGiftDialogFields.paymentMethod || platform?.username !== editGiftDialogFields.username : true)
      );

      if (shouldCreateNewPlatform) {
        const newPlatform = await createDonationFundPlatform({
          variables: {
            eventId: eventId,
            type: editGiftDialogFields.paymentMethod as DonationFundPlatformTypeEnum,
            link: paymentLink,
            username: editGiftDialogFields.username,
          },
        });
        if (newPlatform) {
          const newPlatformData = newPlatform.data?.createDonationFundPlatform;
          await updateRegistryItem({
            variables: getUpdateGiftVariables(newPlatformData?.id),
          });
        } else {
          await updateRegistryItem({
            variables: getUpdateGiftVariables(platform?.id),
          });
        }
      } else {
        await updateRegistryItem({
          variables: getUpdateGiftVariables(existingPlatform ? existingPlatform.id : platform?.id),
        });
      }
      if (photoFilename && product.id) {
        await uploadPhoto({ variables: { assetId: photoFilename, id: productId } });
      }
    } else {
      const newRegistryItem = await createRegistryItem({
        variables: {
          eventId: eventId,
          payload: {
            totalRequested: editGiftDialogFields.qty,
            note: editGiftDialogFields.note,
            isGetCashEquivalent: editGiftDialogFields.isGetCashEquivalentEnabled,
            custom: {
              type: CustomItemDataType.gift,
              title: editGiftDialogFields.title || '',
              url: editGiftDialogFields.link || '',
              priceAsFloatingPointDecimalString: priceToFix.toFixed(2),
              imageUrlForImport: editGiftDialogFields.image || '',
            },
            metadata: {
              source: 'manualScanned',
              sourceMetadata: {
                scannedPrice: prefilledData?.price || '0',
              },
            },
          },
        },
      });
      const id = newRegistryItem.data?.createRegistryItem.id;
      if (photoFilename && id) {
        await uploadPhoto({ variables: { assetId: photoFilename, id } });
      }
      trackImpactRadius('Product_Added');
      actions.resetForm();
      clearPhoto();
      if (showAddressReminderDialog) {
        showAddressReminderDialog();
      }
      identify({ addedGift: true });
      clickAddRegistryItem(
        priceToFix * 100,
        '$',
        editGiftDialogFields.qty,
        editGiftDialogFields.link || '',
        editGiftDialogFields.title || '',
        CustomItemDataType.gift,
        eventId,
        null,
        Date.now(),
        RegistryItemSource.manualScanned,
      );
    }
    editRegistryItemSaveButton({
      product,
      quantity: editGiftDialogFields.qty,
      isGroupGiftingEnabled: editGiftDialogFields.isGroupGiftingEnabled,
      paymentMethod: editGiftDialogFields.paymentMethod || undefined,
      userName: editGiftDialogFields.username || undefined,
      dataExtractionSuccess: Boolean(prefilledData),
      hasAttemptedDataExtraction: hasAttemptedDataExtraction.current,
      isGetCashEquivalent: editGiftDialogFields.isGetCashEquivalentEnabled,
    });
    onClose();
  };
  const minQyt = product?.isGroupGiftingEnabled ? 1 : qtyReceived || 1;
  const quantityError = qtyReceived ? receivedQuantityError() : quantityMinError();
  const isCatalogItem = product && !product?.externallyOwned && !product?.canEditProductData;

  const donationFundFundInfo = useMemo(() => product?.donationFund?.fund as DonationFundFragment | undefined, [product]);
  const defaultPlatform = donationFundInfo?.defaultPlatform;
  const currentPaymentMethod = donationFundFundInfo?.platform?.type || undefined;
  const currentPaymentMethodUsername = donationFundFundInfo?.platform?.username || undefined;

  const formik = useFormik<EditGiftDailogFields>({
    enableReinitialize: true,
    initialValues: {
      currency: isPricedProduct(product) ? getCurrencySymbolFromCurrencyCode(product.currencyCode) : DEFAULT_CURRENCY_SYMBOL,
      price: parseFloat(prefilledData?.regularPrice || '0') || (isPricedProduct(product) ? product.numberPrice.valueOf() : 0),
      qty: product?.requested || 1,
      note: product?.note || '',
      title: prefilledData?.name || product?.title || '',
      link: prefilledUrl || prefilledData?.url || product?.externalUrl || '',
      image: prefilledData?.mainImage?.url || product?.image || '',
      isGetCashEquivalentEnabled: Boolean(product?.isGetCashEquivalent),
      isGroupGiftingEnabled: !!product?.isGroupGiftingEnabled,
      username:
        donationFundFundInfo?.platform?.type && donationFundFundInfo?.platform.type !== DonationFundPlatformTypeEnum.other && donationFundFundInfo?.platform.username
          ? donationFundFundInfo?.platform?.username
          : defaultPlatform?.type !== DonationFundPlatformTypeEnum.other && defaultPlatform?.username
            ? (defaultPlatform?.username as string)
            : '',
      paymentMethod: donationFundFundInfo?.platform?.type || defaultPlatform?.type || DonationFundPlatformTypeEnum.venmo,
      mustHave: product?.mustHave || false,
    },
    onSubmit: handleSubmit,
    validationSchema: Yup.object<EditGiftDailogFields>({
      note: Yup.string().max(512),
      title: isCatalogItem ? undefined : Yup.string().required(translateForm('required')),
      link: isCatalogItem
        ? undefined
        : Yup.string()
            .required(translateForm('required'))
            .test('is-valid-url-and-domain', linkInvalid(), value => Boolean(value) && isValidUrlAndDomain(value)),
      qty: Yup.number().min(minQyt, quantityError).integer().required(translateForm('required')).min(minQyt, quantityError),
      price: isCatalogItem ? undefined : Yup.number().min(0).required(translateForm('required')),
      isGroupGiftingEnabled: Yup.boolean(),
      isGetCashEquivalentEnabled: Yup.boolean(),
      paymentMethod: Yup.mixed<DonationFundPlatformTypeEnum>().required(selectPaymentEmptyError),
      username: Yup.string().when('isGroupGiftingEnabled', {
        is: true,
        then: Yup.string()
          .matches(/^[^@]+$/g, { message: invalidUsernameIsEmailError })
          .required(invalidUsernameError),
        otherwise: Yup.string(),
      }),
      mustHave: Yup.boolean(),
    }),
  });

  const setPaymentMethod = (paymentMethod?: DonationFundPlatformTypeEnum) => {
    formik.setFieldValue('paymentMethod', paymentMethod ? paymentMethod : undefined);

    if (paymentMethod !== currentPaymentMethod && !!product?.donationFund?.received) {
      setIsPaymentMethodWarningShown(true);
    } else if (isPaymentMethodWarningShown) {
      setIsPaymentMethodWarningShown(false);
    }
  };

  const setPaymentMethodUsername = (username?: string) => {
    formik.setFieldValue('username', username ? username : '');

    if (username !== currentPaymentMethodUsername && !!product?.donationFund?.received) {
      setIsPaymentMethodWarningShown(true);
    } else if (isPaymentMethodWarningShown) {
      setIsPaymentMethodWarningShown(false);
    }
  };

  const selectedPaymentMethod: Maybe<PaymentMethodOption> = paymentMethodOptions.find(option => {
    return formik && formik.values && option.value === formik.values.paymentMethod && (formik.values.paymentMethod as string) !== DonationFundPlatformTypeEnum.other;
  });

  const handleDelete = async () => {
    if (!product || !product.id) {
      return;
    }
    if (onRemove) {
      onRemove(product);
    }
  };

  const registryItemLoading = createRegistryItemLoading || updateRegistryItemLoading || updateRegistryItemQuantityLoading;

  const clearPhoto = () => {
    setUpdatedPhoto(undefined);
    setPhotoFilename(undefined);
  };

  const { open: openFilepicker, getPhotoPreviewUrl } = useFilestack({
    containerId: eventId || '',
    transformations: {
      crop: {
        aspectRatio: 1 / 1,
        force: true,
      },
    },
    onUploadDone: async res => {
      const { filename, url } = res.filesUploaded[0];
      const photo = await getPhotoPreviewUrl({
        url,
      });
      setUpdatedPhoto(photo);
      setPhotoFilename(filename);
    },
    maxFiles: 1,
  });

  useEffect(() => {
    const image = prefilledData?.mainImage?.url || product?.image;
    setUpdatedPhoto(image);
  }, [prefilledData?.mainImage?.url, product]);

  const {
    onOpenDisclaimerDialog,
    onClose: onCloseDisclaimerDialog,
    isOpen: isOpenDisclaimerDialog,
    disclaimerFormik,
    agreementAccepted,
    paymentMetadata,
  } = useAppPaymentDisclaimerDialogController(eventId);

  const handleConfirm = useCallback(() => {
    formik.setFieldValue('image', updatedPhoto);
    if (!formik.isSubmitting) {
      formik.submitForm();
    }
  }, [formik, updatedPhoto]);

  const handleDisclaimerBeforeSubmit = useCallback(() => {
    const isPlatformExist = getExistingPlatform({ paymentMethod: formik.values.paymentMethod, username: formik.values.username || '' }, donationFundInfo);
    if (formik.values.isGroupGiftingEnabled && !isPlatformExist && !agreementAccepted) {
      onOpenDisclaimerDialog(createPaymentMetadata({ paymentMethod: formik.values.paymentMethod, username: formik.values.username || '' }));
    } else {
      handleConfirm();
    }
  }, [formik, agreementAccepted, onOpenDisclaimerDialog, handleConfirm, donationFundInfo]);

  const handleCancel = () => {
    formik.resetForm();
    onClose();
  };

  const handleDeleteRegistryItem = async () => {
    await handleDelete();
    onClose();
  };

  const handleTooltipClick = useCallback(
    e => {
      e.preventDefault();
      onClose();
      showCurrencyDialog();
    },
    [onClose, showCurrencyDialog],
  );

  const handleChangeFromPurchaseOption = (value: PurchaseOption) => {
    formik.setFieldValue('isGetCashEquivalentEnabled', value === 'cash');
    formik.setFieldValue('isGroupGiftingEnabled', value === 'groupGift');
  };

  return {
    formik,
    handleDelete,
    registryItemLoading,
    showCurrencyDialog,
    updatedPhoto,
    openFilepicker,
    handleTooltipClick,
    handleDeleteRegistryItem,
    handleCancel,
    handleConfirm: handleDisclaimerBeforeSubmit,
    setPaymentMethod,
    setPaymentMethodUsername,
    selectedPaymentMethod,
    isPaymentMethodWarningShown,
    handleBrandClick,
    isProductExtractionLoading,
    prefilledData,
    extractDataFromUrl,
    showExtractError,
    handleChangeFromPurchaseOption,
    onCloseDisclaimerDialog,
    isOpenDisclaimerDialog,
    disclaimerFormik,
    paymentMetadata,
  };
};
